import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Headphones, BookOpen, ListTree, Lock } from "lucide-react";
import { API } from "@/lib/api";
import { BOOKS } from "@/lib/catalog";

const coverFor = (productId) => BOOKS.find((b) => b.id === productId)?.cover;

function NarrationMeter({ label, done, total }) {
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between font-mono-ui text-[10px] uppercase tracking-[0.18em] text-soft">
        <span>{label} voice</span><span>{pct === 100 ? "Ready" : `${pct}%`}</span>
      </div>
      <div className="h-1 bg-white/10 mt-1.5"><div className="h-1 bg-primary-gold transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function BookRow({ slot, title, book, index }) {
  return (
    <li className="grid sm:grid-cols-[110px_1fr_auto] gap-6 items-center border border-white/10 bg-[#121212]/80 p-5 fade-up" style={{ animationDelay: `${index * 80}ms` }} data-testid={`admin-book-${slot}`}>
      {coverFor(slot) ? <img src={coverFor(slot)} alt={title} className="w-[110px] aspect-[2/3] object-cover border border-white/10" /> : <div className="w-[110px] aspect-[2/3] bg-white/5" />}
      <div>
        <h2 className="font-serif-display text-2xl sm:text-3xl">{title}</h2>
        {book ? (
          <>
            <p className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-soft text-xs font-mono-ui uppercase tracking-[0.15em]">
              <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {book.page_count} pages</span>
              <span className="flex items-center gap-1.5"><ListTree className="w-3.5 h-3.5" /> {(book.chapters_v2 || book.chapters || []).length} chapter{(book.chapters_v2 || book.chapters || []).length === 1 ? "" : "s"}</span>
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4 max-w-md">
              <NarrationMeter label="Male" done={book.narrated_pages?.male || 0} total={book.page_count} />
              <NarrationMeter label="Female" done={book.narrated_pages?.female || 0} total={book.page_count} />
            </div>
          </>
        ) : (
          <p className="text-soft text-sm mt-2">No manuscript uploaded yet — add one in the dashboard's E-Reader tab.</p>
        )}
      </div>
      {book && (
        <Link to={`/reader/read/${book.id}`} state={{ from: "/admin/books" }} data-testid={`admin-book-open-${slot}`} className="btn-gold px-6 py-4 font-mono-ui text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2">
          <Headphones className="w-4 h-4" /> Read & listen
        </Link>
      )}
    </li>
  );
}

export default function AdminBooks() {
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    axios.get(`${API}/admin/reader`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (data === undefined) return <div className="pt-40 flex justify-center"><Loader2 className="w-6 h-6 text-primary-gold animate-spin" /></div>;
  if (data === null) {
    return (
      <div className="pt-40 pb-24 px-6 max-w-md mx-auto text-center" data-testid="admin-books-locked">
        <Lock className="w-6 h-6 text-primary-gold mx-auto" />
        <h1 className="font-serif-display text-4xl mt-4">Admins only</h1>
        <button onClick={() => navigate("/admin")} data-testid="admin-books-login" className="btn-gold mt-8 px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.18em]">Admin login</button>
      </div>
    );
  }
  const bySlot = Object.fromEntries(data.books.map((b) => [b.product_id, b]));
  return (
    <div className="pt-32 pb-24 px-6 sm:px-8 max-w-6xl mx-auto" data-testid="admin-books-page">
      <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold">Admin · Author review</p>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-4">
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Review your books</h1>
        <Link to="/admin" data-testid="admin-books-dashboard" className="font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold">← Dashboard</Link>
      </div>
      <p className="text-soft mt-4 max-w-2xl">Open any book exactly as readers see it — read along, switch between the male and female narrator, use chapters, bookmarks and the sleep timer.</p>
      <ul className="mt-12 space-y-5">
        {Object.entries(data.slots).map(([slot, title], i) => <BookRow key={slot} slot={slot} title={title} book={bySlot[slot]} index={i} />)}
      </ul>
    </div>
  );
}
